<mat-card class="chart-card">
  <mat-card-header>
    <mat-card-title>{{ 'analytics.marketSummaryDashboard' | transloco }}</mat-card-title>
    <mat-card-subtitle>{{ 'analytics.comprehensiveOverview' | transloco }}</mat-card-subtitle>
  </mat-card-header>

  <mat-card-content>
    <!-- SSR placeholder -->
    <div *ngIf="!isBrowser" class="ssr-placeholder">
      <p>{{ 'analytics.loadingDashboard' | transloco }}</p>
    </div>

    <!-- Render dashboard only in browser -->
    <ng-container *ngIf="isBrowser">
      <!-- Loading state -->
      <div *ngIf="loading" class="loading-overlay">
        <mat-spinner diameter="50"></mat-spinner>
        <p>{{ 'analytics.loadingAnalytics' | transloco }}</p>
      </div>

      <!-- Error state -->
      <div *ngIf="error && !loading" class="error-message">
        <p>{{ error }}</p>
      </div>

      <!-- Dashboard Grid -->
      <div *ngIf="!loading && !error && summaryData" class="dashboard-grid">
        <!-- Top Metrics Row -->
        <div class="metrics-row">
          <!-- Jobs Metrics Section -->
          <div class="metrics-section jobs-section">
            <div class="section-header">
              <h3>
                <i class="fas fa-briefcase"></i>
                {{ 'analytics.jobsMetrics' | transloco }}
              </h3>
              <span class="trend-indicator" [ngClass]="getTrendClass(summaryData.trends.jobs)">
                <i class="fas {{ getTrendIcon(summaryData.trends.jobs) }}"></i>
                {{ 'analytics.trend.' + summaryData.trends.jobs | transloco }}
              </span>
            </div>
            <div class="metrics-grid">
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.totalJobs' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.jobs.total }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.newJobs' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.jobs.new }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.filledJobs' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.jobs.filled }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.fillRate' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.jobs.fillRate.toFixed(1) }}%</h2>
              </div>
            </div>
          </div>

          <!-- Candidates Metrics Section -->
          <div class="metrics-section candidates-section">
            <div class="section-header">
              <h3>
                <i class="fas fa-users"></i>
                {{ 'analytics.candidatesMetrics' | transloco }}
              </h3>
              <span class="trend-indicator" [ngClass]="getTrendClass(summaryData.trends.candidates)">
                <i class="fas {{ getTrendIcon(summaryData.trends.candidates) }}"></i>
                {{ 'analytics.trend.' + summaryData.trends.candidates | transloco }}
              </span>
            </div>
            <div class="metrics-grid">
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.totalCandidates' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.candidates.total }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.newCandidates' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.candidates.new }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.appliedCandidates' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.candidates.applied }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.hiredCandidates' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.candidates.hired }}</h2>
              </div>
            </div>
          </div>
        </div>

        <!-- Applications & Supply/Demand Section -->
        <div class="combined-section">
          <div class="metrics-section applications-section">
            <div class="section-header">
              <h3>
                <i class="fas fa-file-alt"></i>
                {{ 'analytics.applicationsMetrics' | transloco }}
              </h3>
              <span class="trend-indicator" [ngClass]="getTrendClass(summaryData.trends.applications)">
                <i class="fas {{ getTrendIcon(summaryData.trends.applications) }}"></i>
                {{ 'analytics.trend.' + summaryData.trends.applications | transloco }}
              </span>
            </div>
            <div class="metrics-grid">
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.totalApplications' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.applications.total }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.avgPerJob' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.applications.avgPerJob.toFixed(1) }}</h2>
              </div>
              <div class="metric-item">
                <p class="metric-label">{{ 'analytics.conversionRate' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.applications.conversionRate.toFixed(1) }}%</h2>
              </div>
            </div>
          </div>

          <div class="metrics-section supply-demand-section">
            <div class="section-header">
              <h3>
                <i class="fas fa-exchange-alt"></i>
                {{ 'analytics.supplyDemand' | transloco }}
              </h3>
            </div>
            <div class="supply-demand-content">
              <div class="ratio-display">
                <p class="metric-label">{{ 'analytics.supplyDemandRatio' | transloco }}</p>
                <h2 class="metric-value">{{ summaryData.metrics.supplyDemand.ratio.toFixed(2) }}</h2>
              </div>
              <span class="interpretation-badge" [ngClass]="getInterpretationClass(summaryData.metrics.supplyDemand.interpretation)">
                {{ getInterpretationText(summaryData.metrics.supplyDemand.interpretation) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Tables - Flexible Layout -->
        <div class="tables-container">
          <!-- Top Categories Table -->
          <div class="table-section">
            <div class="section-header">
              <h3>
                <i class="fas fa-folder"></i>
                {{ 'analytics.topCategories' | transloco }}
              </h3>
            </div>
            <div class="table-container">
              <table *ngIf="summaryData.topCategories.length > 0" class="summary-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.category' | transloco }}</th>
                    <th>{{ 'analytics.jobs' | transloco }}</th>
                    <th>{{ 'analytics.candidates' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of displayedCategories">
                    <td class="name-cell">{{ translateCategory(item.categoryName) }}</td>
                    <td>{{ item.jobs }}</td>
                    <td>{{ item.candidates }}</td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="shouldShowMoreCategories" class="show-more-container">
                <button mat-button class="show-more-btn" (click)="toggleCategories()">
                  {{ showAllCategories ? ('filters.showLess' | transloco) : ('filters.showMore' | transloco) }}
                  <i class="fas" [ngClass]="showAllCategories ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>
              </div>

              <div *ngIf="summaryData.topCategories.length === 0" class="no-data">
                <p>{{ 'analytics.noCategoryData' | transloco }}</p>
              </div>
            </div>
          </div>

          <!-- Top Positions Table -->
          <div class="table-section">
            <div class="section-header">
              <h3>
                <i class="fas fa-briefcase"></i>
                {{ 'analytics.topPositions' | transloco }}
              </h3>
            </div>
            <div class="table-container">
              <table *ngIf="summaryData.topPositions.length > 0" class="summary-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.position' | transloco }}</th>
                    <th>{{ 'analytics.jobs' | transloco }}</th>
                    <th>{{ 'analytics.candidates' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of displayedPositions">
                    <td class="name-cell">{{ translatePosition(item.positionName) }}</td>
                    <td>{{ item.jobs }}</td>
                    <td>{{ item.candidates }}</td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="shouldShowMorePositions" class="show-more-container">
                <button mat-button class="show-more-btn" (click)="togglePositions()">
                  {{ showAllPositions ? ('filters.showLess' | transloco) : ('filters.showMore' | transloco) }}
                  <i class="fas" [ngClass]="showAllPositions ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>
              </div>

              <div *ngIf="summaryData.topPositions.length === 0" class="no-data">
                <p>{{ 'analytics.noPositionData' | transloco }}</p>
              </div>
            </div>
          </div>

          <!-- Top Locations Table -->
          <div class="table-section">
            <div class="section-header">
              <h3>
                <i class="fas fa-map-marker-alt"></i>
                {{ 'analytics.topLocations' | transloco }}
              </h3>
            </div>
            <div class="table-container">
              <table *ngIf="summaryData.topLocations.length > 0" class="summary-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.location' | transloco }}</th>
                    <th>{{ 'analytics.jobs' | transloco }}</th>
                    <th>{{ 'analytics.candidates' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of displayedLocations">
                    <td class="name-cell">{{ formatLocation(item.city, item.country) }}</td>
                    <td>{{ item.jobs }}</td>
                    <td>{{ item.candidates }}</td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="shouldShowMoreLocations" class="show-more-container">
                <button mat-button class="show-more-btn" (click)="toggleLocations()">
                  {{ showAllLocations ? ('filters.showLess' | transloco) : ('filters.showMore' | transloco) }}
                  <i class="fas" [ngClass]="showAllLocations ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>
              </div>

              <div *ngIf="summaryData.topLocations.length === 0" class="no-data">
                <p>{{ 'analytics.noLocationData' | transloco }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </mat-card-content>
</mat-card>
